import type {
  DocumentReaderUploadStatus,
  IDocumentReaderUploadCountsView,
  IDocumentReaderUploadLimits,
} from '@commenda/api/commenda-ai/document-reader/types';
import type { CommendaAiUploadFile } from '@commenda/api/commenda-ai/types';

export enum DataRoomFileState {
  UPLOADING = 'UPLOADING',
  STORED = 'STORED',
  FAILED = 'FAILED',
}

/** What a drop of files does to the message's data room. */
export enum DataRoomDropKind {
  CREATE = 'CREATE',
  /** Waits for the room being created or re-read, then is added to it. */
  QUEUE = 'QUEUE',
  APPEND = 'APPEND',
  RESUME = 'RESUME',
}

/** One local file of a data room, paired with the file the backend created for it. */
export interface IDataRoomLocalFile {
  selectedFile: CommendaAiUploadFile;
  fileId: string;
  batchIndex: number;
  partCount: number;
  partSizeBytes: number;
  /** Part number to the ETag its PUT answered, for every part S3 holds. */
  storedEntityTags: Map<number, string>;
}

export interface IDataRoomFileProgress {
  fileId: string;
  name: string;
  sizeBytes: number;
  sentBytes: number;
  state: DataRoomFileState;
  error: string | null;
  /** Path, size and last-modified time of the local file; null for a row read back from the server. */
  duplicateKey: string | null;
}

export interface IDataRoomUploadProgress {
  files: IDataRoomFileProgress[];
  sentBytes: number;
  totalBytes: number;
  isStored: boolean;
}

/** Kept in sessionStorage while a room uploads. Only ids and numbers: no names and no part URLs. */
export interface IDataRoomResumeRecord {
  uploadId: string;
  fileCount: number;
  /** Last-modified time of each file, by batch and position, so a re-selected file is the same file. */
  lastModifiedByBatch: number[][];
  /** Files removed before sending; fileCount still counts them. */
  removedFileIds: string[];
}

/** File count and bytes of the set a drop is added to. */
export interface IDataRoomSetSize {
  fileCount: number;
  totalBytes: number;
}

/** What is already in the set a drop joins: the rows shown and the files waiting to be added. */
export interface IDataRoomSetContents {
  rows: readonly IDataRoomFileProgress[];
  waitingUploads: readonly CommendaAiUploadFile[];
}

/** A drop after its empty files and duplicates are set aside, with the limit refusal if any. */
export interface IDataRoomDropCheck {
  uploads: CommendaAiUploadFile[];
  emptyNames: string[];
  duplicateNames: string[];
  limitError: string | null;
}

export interface IDataRoomDropState {
  hasResumeRecord: boolean;
  hasSession: boolean;
  /** A room is being created or re-read and has no session yet. */
  isCreating: boolean;
  /** A resumed room that still misses files the user has to choose again. */
  isMissingFiles: boolean;
  isUploading: boolean;
}

export interface IDataRoomPartRange {
  partNumber: number;
  start: number;
  end: number;
}

export interface IDataRoomResumeMatch {
  files: IDataRoomLocalFile[];
  unmatchedNames: string[];
}

/** Runs a task once one of a fixed number of slots is free. */
export type TDataRoomLimiter = <T>(task: () => Promise<T>) => Promise<T>;

export interface IDataRoomTransferContext {
  uploadId: string;
  maxPartUrlsPerRequest: number;
  signal: AbortSignal;
  /** Shared by every file of the room, so no more than PARTS_IN_FLIGHT PUTs run at once. */
  limitPart: TDataRoomLimiter;
  onFileBytes: (fileId: string, sentBytes: number) => void;
  /** The file's own signal, so removing one file stops only its parts. */
  signalFor?: (fileId: string) => AbortSignal;
}

/** The open upload of one message, as the hook holds it. */
export interface IDataRoomUploadSession {
  uploadId: string;
  files: IDataRoomLocalFile[];
  completedFileIds: Set<string>;
  controller: AbortController;
  limitPart: TDataRoomLimiter;
  /** One per file under the room's controller, so removing a file stops only its parts. */
  fileControllers: Map<string, AbortController>;
  removingFileIds: Set<string>;
  /** A resumed room whose files were not all chosen again. */
  isMissingFiles: boolean;
}

export interface IDataRoomFileOutcome {
  success: boolean;
  /** A failure no retry can fix (cancelled, or the bucket hides the ETag), which stops the room. */
  isFatal: boolean;
  uploadStatus: DocumentReaderUploadStatus | null;
  error: string | null;
}

export interface ICreateDataRoomParams {
  uploads: readonly CommendaAiUploadFile[];
  limits: Readonly<IDocumentReaderUploadLimits>;
  /** Null on a new chat: the first send links the upload to the chat it creates. */
  conversationId: string | null;
  companyId: number | null;
  signal: AbortSignal;
  onRecordChange: (record: IDataRoomResumeRecord) => void;
}

export interface IAppendDataRoomParams {
  uploadId: string;
  uploads: readonly CommendaAiUploadFile[];
  limits: Readonly<IDocumentReaderUploadLimits>;
  signal: AbortSignal;
  onBatchAdded: (batchIndex: number, lastModified: number[]) => void;
}

/** A failure keeps the files of the batches already added; they are part of the upload. */
/** Changes the saved resume record, when there is one. */
export type TDataRoomRecordUpdate = (
  change: (record: IDataRoomResumeRecord) => IDataRoomResumeRecord
) => void;

export interface IAddDataRoomDropParams {
  session: IDataRoomUploadSession;
  uploads: readonly CommendaAiUploadFile[];
  limits: Readonly<IDocumentReaderUploadLimits>;
  updateRecord: TDataRoomRecordUpdate;
}

export type TAppendDataRoomResult =
  | {
      success: true;
      files: IDataRoomLocalFile[];
      skippedEmptyFileNames: string[];
    }
  | {
      success: false;
      error: string;
      files: IDataRoomLocalFile[];
      skippedEmptyFileNames: string[];
    };

export type TRemoveDataRoomFileResult =
  | { success: true; upload: IDocumentReaderUploadCountsView }
  | { success: false; error: string };

export type TCreateDataRoomResult =
  | {
      success: true;
      uploadId: string;
      files: IDataRoomLocalFile[];
      skippedEmptyFileNames: string[];
    }
  | { success: false; error: string };

export type TLoadDataRoomResumeResult =
  | {
      success: true;
      status: DocumentReaderUploadStatus;
      files: IDataRoomLocalFile[];
      storedRows: IDataRoomFileProgress[];
      unmatchedNames: string[];
      abortedNames: string[];
      /** Removed before the reload while the server still lists them, so removed again. */
      pendingRemovalFileIds: string[];
    }
  | {
      success: false;
      status: DocumentReaderUploadStatus | null;
      error: string;
    };
